// Pure display logic for the redesigned Shippers table. Everything here is deterministic
// so it can be unit tested without mounting the table.
import {
    deliveryProfileCardFor,
    isFlexCenterRow,
    pricingDetailOf,
    pricingSummaryOf,
    settingCardsFor,
} from '../Carriers/utils';

export const ROUTING_AUTO = 'AUTO';
export const ROUTING_SHOP = 'SHOP';
export const ROUTING_MARKETPLACE = 'MARKETPLACE';

const SHIPPER_TYPE_BY_MODE = {
    [ROUTING_AUTO]: 'AUTO_ASSIGN',
    [ROUTING_SHOP]: 'RATE_SHOP',
    [ROUTING_MARKETPLACE]: 'MARKETPLACE',
};

/**
 * The routing chip for a `shipper_type`; NULL and unknown values are Marketplace. There's one chip
 * per value, so if Marketplace ever loses its chip, shippers get stranded there.
 */
export const routingModeFor = (shipperType) =>
    Object.keys(SHIPPER_TYPE_BY_MODE).find((mode) => SHIPPER_TYPE_BY_MODE[mode] === shipperType) || ROUTING_MARKETPLACE;

/** The `shipper_type` a routing chip writes. Throws on an unknown mode rather than guessing one. */
export const shipperTypeForRouting = (mode) => {
    const shipperType = SHIPPER_TYPE_BY_MODE[mode];
    if (!shipperType) throw new Error(`Unknown routing mode: ${mode}`);
    return shipperType;
};

// "Riverstone Logistics" → "RL". Falls back to a single glyph so the avatar is never blank.
export const initialsFor = (name) => {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '?';
    return words
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
};

// A shipper can be on several delivery profiles. The table cell is narrow, so it shows the
// first name and "+N" for the rest; `all` is the full list for the hover title.
export const profileSummaryFor = (assignment) => {
    const names = (assignment?.profileNames || []).filter(Boolean);
    if (!names.length) return null;
    return { first: names[0], more: names.length - 1, all: names.join(', ') };
};

// A middle-mile or warehouse rate is priced separately, so it never replaces final-mile carrier pay.
const FINAL_MILE_LEGS = ['E2E', 'FINAL_MILE', 'GIG'];

// TODO: move into @onward-delivery/core and share it with onward-functions so the two can't drift.
// Copy of TARIFFLESS_ALGO_TYPES in onward-functions pricing/constants.js, plus price()'s WAREHOUSE_DEFAULT exemption.
const TARIFFLESS_ALGO_TYPES = [
    'BUNGII',
    'CURRI',
    'DISPATCHIT',
    'FRAYT',
    'CEVA',
    'CUSTOMCO',
    'CXT_API',
    'GRASSHOPPER',
    'METRO',
    'OLD_DOMINION',
    'TAI',
    'TFORCE',
    'EXTERNAL',
    'WAREHOUSE_DEFAULT',
];

const isLiveFinalMileRate = (override) => override.enabled && FINAL_MILE_LEGS.includes(override.override_type);

const isRateMissingTariff = (override) => !override.tariff_id && !TARIFFLESS_ALGO_TYPES.includes(override.algo_type);

const finalMileSummaryOf = (overrides, tariffsById) => {
    const finalMileOverrides = overrides.filter((override) => FINAL_MILE_LEGS.includes(override.override_type));

    return pricingSummaryOf(finalMileOverrides, tariffsById);
};

// Must agree with onward-functions' enrichOverrides: own rate, then org default, then carrier standard, then marketplace.
const appliedFinalMilePricingFor = ({ carrierPricing, defaultOverrides, standardOverrides, tariffsById }) => {
    const ownPricing = finalMileSummaryOf(carrierPricing, tariffsById);
    // The backend pays the first rate in the tier that prices, so only an all-untariffed tier goes unpaid.
    const ownFinalMileRates = carrierPricing.filter(isLiveFinalMileRate);
    if (ownFinalMileRates.length && ownFinalMileRates.every(isRateMissingTariff)) {
        return { source: 'OWN', detail: 'Rate missing a tariff', missingTariff: true };
    }
    if (ownPricing.pricingKeys.length) return { source: 'OWN', detail: pricingDetailOf(ownPricing) };

    // The backend skips a default whose tariff was deleted.
    const tariffedDefaults = defaultOverrides.filter((override) => override.tariff_id);
    const defaultPricing = finalMileSummaryOf(tariffedDefaults, tariffsById);
    if (defaultPricing.pricingKeys.length) {
        return { source: 'DEFAULT', detail: `Default: ${pricingDetailOf(defaultPricing)}` };
    }

    const standardFinalMileRates = standardOverrides.filter(isLiveFinalMileRate);
    if (standardFinalMileRates.length && standardFinalMileRates.every(isRateMissingTariff)) {
        return { source: 'STANDARD', detail: 'Carrier standard: rate missing a tariff', missingTariff: true };
    }
    const standardPricing = finalMileSummaryOf(standardOverrides, tariffsById);
    if (standardPricing.pricingKeys.length) {
        return { source: 'STANDARD', detail: `Carrier standard: ${pricingDetailOf(standardPricing)}` };
    }

    return { source: 'MARKETPLACE', detail: 'Marketplace rate' };
};

/**
 * Each shipper's setup cards, one entry per carrier it works with. Contractors are paid per Flex Center, so they get no setup here.
 * @returns {Object<string, { carrierRow: Object, shipper: Object, cards: Object[], appliedPricing?: Object }[]>} keyed by shipper client_id
 */
export const carrierSetupsByShipperFrom = ({
    carrierRows,
    shipperGroupsByCarrier,
    defaultPricingByCarrier,
    standardPricingByCarrier,
    tariffsById,
}) =>
    carrierRows.reduce((setupsByShipper, carrierRow) => {
        if (carrierRow.isContractor || carrierRow.isMarketplace) return setupsByShipper;

        const isFlexCenter = isFlexCenterRow(carrierRow);
        const defaultPricing = defaultPricingByCarrier[carrierRow.client_id];
        const standardOverrides = standardPricingByCarrier[carrierRow.client_id] || [];
        // PricingModal offers final mile to a carrier with no legs on file, so only a listed non-final-mile set opts out.
        const carrierLegs = carrierRow.carrier.supply_chain_legs || [];
        const runsFinalMile = !carrierLegs.length || carrierLegs.some((leg) => FINAL_MILE_LEGS.includes(leg));
        const shippers = shipperGroupsByCarrier[carrierRow.client_id].flatMap((group) => group.shippers);
        for (const shipper of shippers) {
            const setups = (setupsByShipper[shipper.client_id] ??= []);
            if (isFlexCenter) {
                setups.push({ carrierRow, shipper, cards: [deliveryProfileCardFor(shipper)] });
                continue;
            }
            if (!runsFinalMile) {
                const carrierCards = settingCardsFor([shipper], defaultPricing);
                // The default_pricing card opens the org-wide default editor, which the Shippers tab must not reach.
                const cards = carrierCards.map((card) =>
                    card.key === 'default_pricing'
                        ? { ...card, key: 'pricing', detail: 'No pricing', actionLabel: 'Set pricing' }
                        : card
                );
                setups.push({ carrierRow, shipper, cards });
                continue;
            }

            const appliedPricing = appliedFinalMilePricingFor({
                carrierPricing: shipper.settings.carrierPricing,
                defaultOverrides: defaultPricing.overrides,
                standardOverrides,
                tariffsById,
            });
            const isPricingSet = appliedPricing.source !== 'MARKETPLACE';
            let statusLabel = isPricingSet ? 'APPLIED' : 'NONE';
            if (appliedPricing.missingTariff) statusLabel = 'NEEDS TARIFF';
            const pricingCard = {
                key: 'pricing',
                title: 'Pricing',
                detail: appliedPricing.detail,
                statusLabel,
                satisfied: isPricingSet && !appliedPricing.missingTariff,
                actionLabel: isPricingSet ? 'Update' : 'Set pricing',
            };
            const carrierCards = settingCardsFor([shipper], defaultPricing);
            const cards = carrierCards.map((card) => {
                const isPricingCard = card.key === 'pricing' || card.key === 'default_pricing';

                return isPricingCard ? pricingCard : card;
            });
            setups.push({ carrierRow, shipper, cards, appliedPricing });
        }

        return setupsByShipper;
    }, {});

export const pluralize = (count, singular, plural) => (count === 1 ? singular : plural || `${singular}s`);
